import { Head, Link, router } from "@inertiajs/react"

import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

interface Todo {
  id: number
  title: string
  due_date: string | null
  manager: string | null
  customer: string | null
  case_name: string | null
  vendor: string | null
  status: number
  overdue: boolean
}
interface Filters {
  customer_id: number | null
  case_id: number | null
  vendor_id: number | null
  employee_id: number | null
}
interface FilterContext {
  type: string
  name: string
}
interface Props {
  todos: Todo[]
  filters: Filters
  filter_context: FilterContext | null
}

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Marketing Todos", href: "/marketing_todos" },
]

function hasActiveFilters(filters: Filters): boolean {
  return !!(
    filters.customer_id ??
    filters.case_id ??
    filters.vendor_id ??
    filters.employee_id
  )
}

export default function Index({ todos, filters, filter_context }: Props) {
  const clearFilters = () => {
    router.get("/marketing_todos", {}, { preserveState: true })
  }

  const pageTitle = filter_context
    ? `${filter_context.name} Tasks`
    : "Marketing Todos"

  const contextLabel = filter_context
    ? `Pending ${filter_context.type} Tasks`
    : "Pending Tasks"

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={pageTitle} />
      <div className="p-6">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold">{contextLabel}</h1>
            {filter_context && (
              <p className="text-hpi-med-gray text-sm">
                Filtered by {filter_context.type}: {filter_context.name}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            {hasActiveFilters(filters) && (
              <button
                onClick={clearFilters}
                className="hover:bg-hpi-pale-gray rounded border px-4 py-2 text-sm"
              >
                Clear Filters
              </button>
            )}
            <Link
              href="/marketing_todos/new"
              className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-white"
            >
              Add Todo
            </Link>
          </div>
        </div>
        {todos.length > 0 ? (
          <table className="border-hpi-light-gray w-full border-collapse border">
            <thead>
              <tr className="bg-hpi-pale-gray">
                <th className="border px-4 py-2 text-left">Title</th>
                <th className="border px-4 py-2 text-left">Due Date</th>
                <th className="border px-4 py-2 text-left">For</th>
                <th className="border px-4 py-2 text-left">Assigned To</th>
                <th className="border px-4 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {todos.map((todo) => (
                <tr
                  key={todo.id}
                  className={todo.overdue ? "bg-hpi-light-red/30" : ""}
                >
                  <td className="border px-4 py-2">
                    <Link
                      href={`/marketing_todos/${todo.id}`}
                      className="text-hpi-active-blue hover:text-hpi-active-blue"
                    >
                      {todo.title}
                    </Link>
                  </td>
                  <td className="border px-4 py-2">
                    {todo.due_date}
                    {todo.overdue && (
                      <span className="text-hpi-red ml-2 text-sm">Overdue</span>
                    )}
                  </td>
                  <td className="border px-4 py-2">
                    {todo.case_name ?? todo.customer ?? todo.vendor ?? "—"}
                  </td>
                  <td className="border px-4 py-2">{todo.manager}</td>
                  <td className="border px-4 py-2">
                    <Link
                      href={`/marketing_todos/${todo.id}/edit`}
                      className="text-hpi-active-blue hover:text-hpi-active-blue"
                    >
                      Edit
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="text-hpi-med-gray">No todos found.</p>
        )}
      </div>
    </AppLayout>
  )
}
